/**
 * The name we show a user by (SKU-1524 step 2a).
 *
 * Mirrors `packages/api/src/user/displayName.ts`. Deliberately duplicated rather
 * than shared: `@sku-manager/shared` is a runtime dependency for both the API and
 * the web bundle, and a two-line string join does not justify adding web weight
 * or a build-order edge for it.
 *
 * There is no fallback to the email address. Both name parts are mandatory as of
 * step 1 — the columns are NOT NULL and every creation path enforced them long
 * before that — so "no name recorded" is not a state the UI should be handed.
 *
 * `User.name` is on its way out: step 2b un-declares it and step 3 drops the
 * column. Nothing here should read it.
 */

/**
 * Shown when a name cannot be formed. Explicit, not blank.
 *
 * The first version of this returned `''` for absent parts and the present part
 * alone for a partial name, which rendered a missing name as ordinary content —
 * an empty table cell or a half-name — with nothing to indicate anything was
 * wrong. Since both parts are NOT NULL in the database, an absent one means a
 * query forgot to select it or the field is genuinely null in a way that should
 * be visible. `N/A` makes that a question someone asks instead of a blank
 * nobody notices.
 */
export const USER_NAME_UNAVAILABLE = 'N/A';

/** Avatar equivalent of USER_NAME_UNAVAILABLE. */
export const USER_INITIALS_UNAVAILABLE = '?';

export interface NamedUser {
  firstName?: string | null;
  lastName?: string | null;
}

/** Both parts, trimmed — or `null` when either is missing. */
const nameParts = (user: NamedUser): [string, string] | null => {
  // Trimmed per part, not just the join: padded input would otherwise render
  // ragged internal spacing that no UI would explain.
  const first = user.firstName?.trim() ?? '';
  const last = user.lastName?.trim() ?? '';

  // BOTH required. A half-name is not a name we can show as if it were complete;
  // rendering "Ada" for a record missing its surname hides the same gap more
  // quietly than an empty string does.
  return first && last ? [first, last] : null;
};

export const userDisplayName = (user: NamedUser): string => {
  const parts = nameParts(user);
  return parts ? parts.join(' ') : USER_NAME_UNAVAILABLE;
};

/**
 * Avatar initials, derived from the same two mandatory parts.
 *
 * Taken from firstName/lastName directly rather than by splitting a joined
 * string: splitting produced three initials for "Ada Byron Lovelace" and one for
 * a single-word name, and it silently produced initials from an EMAIL wherever
 * the old `name || email` fallback had fired.
 */
export const userInitials = (user: NamedUser): string => {
  const parts = nameParts(user);
  return parts
    ? parts
        .map((part) => part[0])
        .join('')
        .toUpperCase()
    : USER_INITIALS_UNAVAILABLE;
};
